import { getAccessTokenExpiryMs } from '@features/authentication/utils/authentication.utils';

import { readCurrentToken, refreshAccessToken, type RefreshClientApi } from './access-token-refresh';

export const REFRESH_SKEW_MS = 60_000;

// Random 0–5s spread so sibling tabs don't all fire in the same millisecond.
const MAX_JITTER_MS = 5_000;

// Never schedule further out than this. Guards against absurd/far-future `exp` values and the
// browser's ~24.8-day setTimeout ceiling (beyond which the delay silently overflows to fire immediately).
const MAX_DELAY_MS = 12 * 60 * 60 * 1000;

let timerId: ReturnType<typeof setTimeout> | null = null;

const computeDelay = (expiryMs: number): number => {
  const jitter = Math.random() * MAX_JITTER_MS;
  const delay = expiryMs - Date.now() - REFRESH_SKEW_MS - jitter;
  return Math.min(Math.max(delay, 0), MAX_DELAY_MS);
};

export const cancelProactiveRefresh = (): void => {
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }
};

export const scheduleProactiveRefresh = (api: RefreshClientApi): void => {
  cancelProactiveRefresh();

  const token = readCurrentToken(api);
  if (!token) {
    return;
  }

  const expiryMs = getAccessTokenExpiryMs(token);
  if (expiryMs === null) {
    return;
  }

  timerId = setTimeout(() => {
    timerId = null;

    // The session may have ended between scheduling and firing.
    const currentToken = readCurrentToken(api);
    if (!currentToken) {
      return;
    }

    void refreshAccessToken(api, currentToken);
  }, computeDelay(expiryMs));
};
